import React, { useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchLogDelete } from '../../redux/slices/logDeleteSlice'
import CMS from '../../components/cms/CMS'
import PageBanner from '../../components/general/PageBanner'
import { Table, Badge, Button, Space, Modal, Tabs, Tag, Tooltip, Card, Typography, Row, Col, message, Spin, Popconfirm } from 'antd'
import { EyeOutlined, InfoCircleOutlined, DeleteOutlined } from '@ant-design/icons'
import moment from 'moment'
import JSONPretty from 'react-json-pretty'
import 'react-json-pretty/themes/monikai.css'
import { useTranslation } from 'react-i18next'

const { Text } = Typography;
const { TabPane } = Tabs;

const LogPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const allLogs = useSelector((state) => state.logGetAll.data);
    const totalLogs = useSelector((state) => state.logGetAll.data);
    const status = useSelector((state) => state.logGetAll.status);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const handleTableChange = (newPagination) => {
        setPagination({
            ...pagination,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        });
    };

    const viewLogDetails = (log) => {
        setSelectedLog(log);
        setDetailModalVisible(true);
    };

    const handleDelete = async (id) => {
        setDeletingId(id);
        await dispatch(fetchLogDelete({ id }));
        message.success(t('log_deleted'));
        setDeletingId(null);
    };

    const serviceNames = useMemo(() => {
        return [...new Set(allLogs?.map(log => log.serviceName).filter(Boolean))];
    }, [allLogs]);
    const statusCodes = useMemo(() => {
        return [...new Set(allLogs?.map(log => log.statusCode).filter(Boolean))];
    }, [allLogs]);

    const formatDate = (dateStr) => dateStr ? moment(dateStr).format('YYYY-MM-DD HH:mm:ss') : '-';
    const parseJSON = (jsonStr) => {
        try { return jsonStr ? JSON.parse(jsonStr) : null; } catch { return jsonStr; }
    };

    const getStatusColor = (statusCode) => {
        if (!statusCode) return 'default';
        if (statusCode >= 200 && statusCode < 300) return 'success';
        if (statusCode >= 300 && statusCode < 400) return 'processing';
        if (statusCode >= 400 && statusCode < 500) return 'warning';
        if (statusCode >= 500) return 'error';
        return 'default';
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
        {
            title: t('status'),
            dataIndex: 'statusCode',
            key: 'statusCode',
            width: 100,
            render: (statusCode) => (
                <Badge status={getStatusColor(statusCode)} text={statusCode ?? '-'} />
            ),
        },
        { title: t('service'), dataIndex: 'serviceName', key: 'serviceName', width: 150, render: (text) => text ?? '-' },
        { title: t('process'), dataIndex: 'process', key: 'process', width: 150, render: (text) => text ?? '-' },
        { title: t('message'), dataIndex: 'message', key: 'message', ellipsis: true, render: (text) => text ?? '-' },
        { title: 'IP', dataIndex: 'ip', key: 'ip', width: 120, render: (text) => text ?? '-' },
        { title: t('date'), dataIndex: 'createdAt', key: 'createdAt', width: 180, render: (text) => formatDate(text) },
        {
            title: t('actions'),
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title={t('view_details')}>
                        <Button type="primary" size="small" icon={<EyeOutlined />} onClick={() => viewLogDetails(record)} />
                    </Tooltip>
                    <Popconfirm
                        title={t('delete_confirm')}
                        onConfirm={() => handleDelete(record.id)}
                        okText={t('yes')}
                        cancelText={t('no')}
                    >
                        <Button
                            type="default"
                            size="small"
                            icon={<DeleteOutlined />}
                            danger
                            loading={deletingId === record.id}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <CMS>
            <PageBanner title={t("saved_log")} description={t("saved_log_desc")} />
            <Card>
                {status === 'loading' ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin size="large" />
                        <p style={{ marginTop: '10px' }}>{t('loading_logs')}</p>
                    </div>
                ) : status === 'failed' ? null : (
                    <Table
                        columns={columns}
                        dataSource={allLogs}
                        rowKey="id"
                        pagination={{
                            ...pagination,
                            total: totalLogs,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showTotal: (total) => t('total_logs', { count: total })
                        }}
                        onChange={handleTableChange}
                        loading={status === 'loading'}
                        scroll={{ x: 'max-content' }}
                    />
                )}
            </Card>

            <Modal
                title={
                    <Space>
                        <InfoCircleOutlined />
                        {t("log_details")}
                        {selectedLog &&
                            <Tag color={getStatusColor(selectedLog.statusCode)}>
                                {selectedLog.statusCode}
                            </Tag>
                        }
                    </Space>
                }
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                width={800}
                footer={[<Button key="close" onClick={() => setDetailModalVisible(false)}>{t("close")}</Button>]}
            >
                {selectedLog && (
                    <Tabs defaultActiveKey="1">
                        <TabPane tab={t("general")} key="1">
                            <Row gutter={[16, 8]}>
                                <Col span={8}><Text strong>{t("id")}:</Text></Col>
                                <Col span={16}>{selectedLog.id}</Col>
                                <Col span={8}><Text strong>{t("service")}:</Text></Col>
                                <Col span={16}>{selectedLog.serviceName ?? '-'}</Col>
                                <Col span={8}><Text strong>{t("status_code")}:</Text></Col>
                                <Col span={16}><Badge status={getStatusColor(selectedLog.statusCode)} text={selectedLog.statusCode ?? '-'} /></Col>
                                <Col span={8}><Text strong>{t("process")}:</Text></Col>
                                <Col span={16}>{selectedLog.process ?? '-'}</Col>
                                <Col span={8}><Text strong>{t("message")}:</Text></Col>
                                <Col span={16}>{selectedLog.message ?? '-'}</Col>
                                <Col span={8}><Text strong>{t("ip")}:</Text></Col>
                                <Col span={16}>{selectedLog.ip ?? '-'}</Col>
                                <Col span={8}><Text strong>{t("user_id")}:</Text></Col>
                                <Col span={16}>{selectedLog.userId ?? '-'}</Col>
                                <Col span={8}><Text strong>{t("date")}:</Text></Col>
                                <Col span={16}>{formatDate(selectedLog.createdAt)}</Col>
                            </Row>
                        </TabPane>
                        <TabPane tab={t("result")} key="2">
                            {selectedLog.result ? (
                                <JSONPretty
                                    id="json-pretty"
                                    data={parseJSON(selectedLog.result)}
                                    style={{ maxHeight: '400px', overflow: 'auto' }}
                                />
                            ) : (<Text type="secondary">{t("no_result_data")}</Text>)}
                        </TabPane>
                    </Tabs>
                )}
            </Modal>
        </CMS>
    );
}

export default LogPage;